import type { ComponentProps, ComponentType } from 'react'

import { Box } from '@enechain/ecloud-designsystem'
import type { FaCalculatorRegular } from '@enechain/ecloud-icons'

import { SideMenuDot } from '~/components/layouts/MainLayout/SideMenuDot'
import { useSideMenuDots } from '~/components/layouts/MainLayout/SideMenuDotsContext'
import {
  type MenuGroup,
  resolveGroupDot,
} from '~/components/layouts/MainLayout/menuGroups'

type IconProps = ComponentProps<typeof FaCalculatorRegular>

const cache = new WeakMap<MenuGroup, ComponentType<IconProps>>()

/**
 * グループの `IconLeft`。ドットがあれば icon に重ねる。DS の menu item には dot 用の
 * slot が無く、`IconLeft` の差し替えなら展開・折りたたみの両方で icon と同じ位置に
 * 出せる (`AnnouncementsMenuIcon` と同じ手)。位置は design
 * `.nav-fa-icon > .nav-menu-alert-icon` (右上 -2px)。
 *
 * ドットは context から読むので、返す component はグループごとに 1 つで安定
 * (render のたびに作ると icon が再 mount する)。
 */
export const getGroupIcon = (group: MenuGroup): ComponentType<IconProps> => {
  const cached = cache.get(group)
  if (cached !== undefined) return cached
  const Icon = group.icon as ComponentType<IconProps>
  const GroupIcon = ({
    'aria-hidden': ariaHidden,
    ...iconProps
  }: IconProps): React.ReactElement => {
    const dot = resolveGroupDot(group, useSideMenuDots())
    if (dot === undefined)
      return <Icon aria-hidden={ariaHidden} {...iconProps} />
    return (
      <Box as="span" position="relative" display="inline-flex">
        <Icon aria-hidden={ariaHidden} {...iconProps} />
        <Box position="absolute" zIndex={3} top="-2px" right="-2px">
          <SideMenuDot dot={dot} rowPulses />
        </Box>
      </Box>
    )
  }
  cache.set(group, GroupIcon)
  return GroupIcon
}
